import { Heart, Clock, History } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { routeBuilders } from '../routes';
import { slugify } from '../../utils/slug';

interface LibraryProps {
  stories: any[];
  likedStories: Set<string>;
  onPlayStory: (story: any) => void;
}

export function Library({ stories, likedStories, onPlayStory }: LibraryProps) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'liked' | 'history'>('liked');

  const likedStoriesData = stories.filter(story => likedStories.has(story.id));
  
  // Mock history data - in a real app, this would come from the backend
  const historyData = stories.slice(0, 3);

  const tabs = [
    { id: 'liked' as const, label: 'Liked Stories', icon: Heart, count: likedStoriesData.length },
    { id: 'history' as const, label: 'Recently Played', icon: History, count: historyData.length },
  ];

  const displayStories = activeTab === 'liked' ? likedStoriesData : historyData;

  return (
    <div className="page-shell">
      {/* Header */}
      <div className="page-header">
        <h1 className="text-4xl font-bold mb-2">Library</h1>
        <p className="text-base-content/60">Your saved stories and listening history</p>
      </div>

      {/* Tabs */}
      <div role="tablist" className="tabs tabs-bordered mb-8">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              role="tab"
              type="button"
              className={`tab gap-2 ${isActive ? 'tab-active' : 'text-base-content/60'}`}
              aria-selected={isActive}
            >
              <Icon className="w-5 h-5" />
              <span>{tab.label}</span>
              <span className={`text-sm ${isActive ? 'text-base-content' : 'text-base-content/50'}`}>
                ({tab.count})
              </span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      {displayStories.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center mx-auto mb-4">
            {activeTab === 'liked' ? (
              <Heart className="w-10 h-10 text-base-content/30" />
            ) : (
              <Clock className="w-10 h-10 text-base-content/30" />
            )}
          </div>
          <h3 className="font-semibold mb-2">
            {activeTab === 'liked' ? 'No liked stories yet' : 'No listening history'}
          </h3>
          <p className="text-sm text-base-content/60">
            {activeTab === 'liked'
              ? 'Stories you like will appear here'
              : 'Stories you listen to will appear here'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {displayStories.map((story, index) => {
            const creatorSlug = story.creatorSlug ?? slugify(story.author);
            const creatorPath = creatorSlug ? routeBuilders.creatorPublic(creatorSlug) : null;
            const seriesSlug = story.slug ?? slugify(story.title);
            const seriesPath = seriesSlug ? routeBuilders.seriesPublic(seriesSlug) : '#';
            return (
              <div
                key={story.id}
                className="flex items-center gap-4 p-4 rounded-2xl hover:bg-base-200 transition-colors cursor-pointer group"
                onClick={() => navigate(seriesPath)}
              >
                <div className="w-20 h-20 rounded-xl overflow-hidden bg-base-200 flex-shrink-0 relative">
                  <img
                    src={story.coverImage}
                    alt={story.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold mb-1 truncate">{story.title}</h3>
                  {creatorPath ? (
                    <Link
                      to={creatorPath}
                      onClick={(e) => e.stopPropagation()}
                      className="text-sm text-base-content/80 hover:text-base-content truncate mb-1 transition-colors block"
                    >
                      {story.author}
                    </Link>
                  ) : (
                    <p className="text-sm text-base-content/70 truncate mb-1">{story.author}</p>
                  )}
                  <p className="text-xs text-base-content/50">{story.duration} · {story.totalChapters} chapters</p>
                </div>
                <div className="flex items-center gap-3">
                  {activeTab === 'history' && (
                    <p className="text-xs text-base-content/60 whitespace-nowrap">2 days ago</p>
                  )}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      onPlayStory(story);
                    }}
                    className="btn btn-outline btn-primary btn-sm"
                  >
                    Play
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Stats Section */}
      {displayStories.length > 0 && (
        <div className="mt-12 p-6 rounded-md bg-base-200">
          <h3 className="font-semibold mb-4">Your Listening Stats</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-2xl font-bold text-primary">42h</p>
              <p className="text-xs text-base-content/70 mt-1">Total listened</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-primary">18</p>
              <p className="text-xs text-base-content/70 mt-1">Stories finished</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-primary">7</p>
              <p className="text-xs text-base-content/70 mt-1">Day streak</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
